import { setBearer, clearBearer } from "./tokens";
import { clearSessionId } from "./session";
import { clearPin } from "./pin";
import { requireTenant } from "./tenant";
import { track } from "./telemetry";

const WEB = import.meta.env.VITE_WEB_HOST!;

type DialogMessage =
  | { ok: true; token: string; mirror_id: string }
  | { ok: false; error: string };

export function isOfficeHost(): boolean {
  return typeof Office?.context?.ui?.displayDialogAsync === "function";
}

export function signInWithDialog(): Promise<{ mirrorId: string }> {
  return new Promise((resolve, reject) => {
    if (!isOfficeHost()) {
      reject(
        new Error(
          "Sign-in is only available when the add-in is opened from inside Excel. " +
          "Install the add-in via Insert → Add-ins, or open Excel and click the myai ribbon button.",
        ),
      );
      return;
    }

    // Pass the tenant explicitly to the dialog URL — the dialog is a fresh
    // browsing context and won't inherit the parent taskpane's query string.
    // The dialog page uses this to resolve to the correct backend host.
    // requireTenant() throws if no tenant is set — Sign in is only reachable
    // post-picker, so this should never trip in practice.
    const tenant = requireTenant();
    const url = `${WEB}/oauth/start?tenant=${encodeURIComponent(tenant)}`;

    Office.context.ui.displayDialogAsync(
      url,
      { height: 60, width: 30, promptBeforeOpen: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(result.error.message));
          return;
        }
        const dialog = result.value;

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          let msg: DialogMessage;
          try {
            msg = JSON.parse((arg as { message: string }).message);
          } catch {
            dialog.close();
            reject(new Error("Malformed dialog payload"));
            return;
          }
          dialog.close();
          if (msg.ok) {
            setBearer(msg.token);
            track("excel.signed_in", { mirror_id: msg.mirror_id });
            resolve({ mirrorId: msg.mirror_id });
          } else {
            reject(new Error(msg.error || "Sign-in failed"));
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
          // Office fires this on user-close, network error, etc.
          // Code 12006 = user closed; others are real errors.
          const errArg = arg as { error: number };
          if (errArg.error === 12006) {
            reject(new Error("Sign-in cancelled"));
          } else {
            reject(new Error(`Dialog error ${errArg.error}`));
          }
        });
      },
    );
  });
}

export function signOut(sessionId: string | null): void {
  // Server-side revocation deferred (no /api/oauth/revoke in v1; the bearer will
  // 401 on next use, which is sufficient for the pilot). Local clear is load-bearing.
  if (sessionId) clearPin(sessionId);
  clearSessionId();
  clearBearer();
  track("excel.signed_out", {});
}
